import wcs from './wcs/index'
import i18n from '../i18n-setup'
import { loadSectionTranslations } from '../i18n-setup'
import { formatPrice, getPropertyByPath } from './utils/string.js'
import prices from '../locales/prices.json'
import algoliaDashFilters from '../helpers/dashFiltersAlgolia'
import { getCurrentLocale, getCurrencySymbol, getCurrentCountry } from './utils/currentCountry'
import { isValidUrl } from './utils/url'
import { pdp } from '../store/modules/pdp'
import { filterImages } from './utils/imageSelect'
import algoliaToggleFilters from '../helpers/toggleFilters'
import { addToBagButtonAction } from './utils/add-to-bag-button'

let currentPlpCategory = window.algoliaConfig?.algoliaCategoryIdentifier || ''
//get this value 'dashFiltersSequence' from the storeconfig
const dashFiltersSequenceArr = sessionStorage.getItem('dashFiltersSequence')?.split(',') || []
const isChanel = currentPlpCategory === 'brands_chanel'
const rangePrice = Boolean(window.algoliaConfig?.priceRange)
  ? JSON.parse(window.algoliaConfig?.priceRange)
  : prices

let _config = null
const getConfig = () => {
  if (_config) return _config
  const isEarlyAccess = Boolean(window.algoliaConfig?.isEarlyAccessParam)
  _config = {
    isEarlyAccess,
    saleBadgeActive: window.algoliaConfig?.saleBadgeActive == 'true',
    isRoxableActive: Boolean(window.algoliaConfig?.RxActive),
    isEngravingActive: Boolean(window.algoliaConfig?.EngravingActive),
    isRemixActive: Boolean(window.algoliaConfig?.RemixActive),
    priceNameConfig: JSON.parse(window.algoliaConfig?.priceName || 'null') || {},
    installmentFeatureActive: Boolean(window.wcs_feature_paymentInstallment?.isActive),
    installmentsOrder: JSON.parse(window.algoliaConfig?.installmentsOrder || 'null'),
    installmentsFeatureActive: Boolean(window.algoliaConfig?.installmentActive),
    sortBadgePath: isEarlyAccess ? 'sortBadge_EarlyAccess' : 'sortBadge_Guest',
    sortDiscountPath: isEarlyAccess ? 'sortDiscount_EarlyAccess' : 'sortDiscount_Guest',
    sortPrice: isEarlyAccess ? 'sortPrice_EarlyAccess' : 'sortPrice_Guest',
  }
  return _config
}

// function used to normalize the response from Algolia plp products list
export const getAlgoliaPlpProductsNormalized = function (res) {
  if (res) {
    const pagination = {}
    pagination.currentPage = parseInt(res.page) || 1
    pagination.pageSize = res.hitsPerPage || undefined
    pagination.totalPages = res.nbPages || undefined
    pagination.totalProducts = res.nbHits || undefined
    const { hits, userData } = res
    const productsNormalized = hits.map(product => getAlgoliaProductNormalized(product, false, 'plp-search', false, userData))
    return {
      products: productsNormalized,
      ...pagination,
    }
  }
  return {}
}

// function used to normalize the response from Algolia instant search
export const getAlgoliaSuggestionsNormalized = function (
  products,
  suggestions,
  contentResult,
  userData,
  redirect,
  explain
) {
  let suggestedWords = []
  let spellcheck = []
  let collationQuery = ''
  let searchTermToReplace = ''
  let productsNormalized = {}
  let normalizedContentResults = {}
  let highlightSuggestedWords = []

  // if the redirect URL is present, the page will be redirected to the given URL on submit
  // added check to manage absolute and relative URLs
  let redirectUrl = redirect?.url || ''
  const countryChunk = `/${getCurrentCountry()}`
  if (redirectUrl) {
    redirectUrl =
      redirectUrl.includes(countryChunk) || isValidUrl(redirectUrl)
        ? redirectUrl
        : `${countryChunk}${redirectUrl}`
  }

  if (products || suggestions?.hits?.length) {
    if (products) {
      productsNormalized = products //already normalized
    }
    if (suggestions) {
      const { hits } = suggestions
      suggestedWords.push(...hits.map(hit => hit.query))
      highlightSuggestedWords.push(...hits.map(hit => hit._highlightResult?.query?.value))
    }
  }

  if (contentResult) {
    normalizedContentResults = getAlgoliaContentNormalized(contentResult)
  }

  return {
    products: productsNormalized || [],
    suggestedWords,
    spellcheck,
    collationQuery,
    searchTermToReplace,
    redirectUrl,
    contentResults: normalizedContentResults,
    highlightSuggestedWords,
    userData: userData || [],
    stopWords: getAlgoliaStopWords(explain?.match?.alternatives) || null,
  }
}

export const buildAlgoliaProductUrl = productUrl =>
  productUrl
    ? `${window.location.origin}/${getCurrentCountry()}${productUrl.startsWith('/') ? '' : '/'
    }${productUrl}`
    : '#'

// --- helpers ---

const firstOf = v => (Array.isArray(v) ? v[0] : v) || undefined
const facetFirst = v => (v?.includes('|') ? v.split('|')[0] : v) || undefined
const plpImg = url => url ? `${url}?impolicy=SGH_bgtransparent&width=640` : undefined

// compute raw pricing state shared across resolvers
const buildPricingState = (prices, priceNameConfig, locale, currency) => {
  const discountedPricePath = discountedPriceValue(prices, priceNameConfig)
  const listPrice = getPropertyByPath(prices, priceNameConfig.ListPrice) || listPriceFallbackValue(prices)
  const offerPrice = getPropertyByPath(prices, discountedPricePath) || ''
  const isOfferPriceDateValid = offerDateValid(prices, discountedPricePath)
  const isOnSale = !!(offerPrice && offerPrice < listPrice && isOfferPriceDateValid)
  const priceRaw = (isOnSale ? offerPrice : listPrice) || undefined
  return { listPrice, offerPrice, isOnSale, isOfferPriceDateValid, priceRaw, locale, currency }
}

const resolvePricing = ({ listPrice, offerPrice, isOnSale, priceRaw, locale, currency }) => ({
  price: isOnSale
    ? getAlgoliaFormattedPrice(offerPrice, locale, currency)
    : listPrice
      ? getAlgoliaFormattedPrice(listPrice, locale, currency)
      : offerPrice
        ? getAlgoliaFormattedPrice(offerPrice, locale, currency)
        : i18n.t('plp_pending_price'),
  priceRaw,
  onSale: isOnSale ? { originalPrice: getAlgoliaFormattedPrice(listPrice, locale, currency) } : undefined,
  currency: currency || undefined,
})

const resolveIdentifiers = ({ attributes, attributes_translated, partnumberId, url, remixableUrl }) => {
  const brand = attributes.BRAND || undefined
  return {
    brand,
    title: brand,
    upc: partnumberId || undefined,
    mfpartnumber: partnumberId || undefined,
    frameColor: facetFirst(attributes_translated?.FRONT_COLOR_FACET),
    lensColor: facetFirst(attributes_translated?.LENS_COLOR_FACET),
    localizedColorLabel: firstOf(attributes_translated.FRONT_COLOR),
    modelName: getLangValue(firstOf(attributes?.MODEL_NAME), firstOf(attributes_translated?.MODEL_NAME)),
    displaySku: firstOf(attributes.DISPLAYSKU),
    frameShape: firstOf(attributes.FRAME_SHAPE),
    frameSizeDisplay: firstOf(attributes.FRAME_SIZE_DISPLAY),
    modelCode: firstOf(attributes.MODEL_CODE_DISPLAY),
    sku: firstOf(attributes.SKU),
    lensTechnology: Array.isArray(attributes.LENS_TREATMENT) ? attributes.LENS_TREATMENT.join(',') : attributes.LENS_TREATMENT || undefined,
    frameTechnology: attributes_translated.FRAME_MATERIAL_FACET || undefined,
    pdpURL: buildAlgoliaProductUrl(url),
    URL: buildAlgoliaProductUrl(url),
    customizeUrl: buildAlgoliaProductUrl(remixableUrl) || undefined,
  }
}

const resolveImages = (item, filterOnModel, componentSection) => {
  const images = getPlpImages(item, filterOnModel, componentSection)
  const imgHover = plpImg(images[1]?.url)
  return {
    img: plpImg(images[0]?.url),
    imgHover,
    imgPreview: imgHover,
    imageIsOnModel: images[0]?.name === 'On model',
    hoverImageIsOnModel: images[1]?.name === 'On model',
  }
}

const resolveFlags = (item, { isEngravingActive, isRemixActive, isRoxableActive }) => {
  const { attributes } = item
  const isLoyaltyActive = sessionStorage.getItem('isLoyaltyProgramEnabled') === 'true'
  return {
    isPolarized: !!(attributes.POLARIZED && attributes.POLARIZED !== 'FALSE'),
    isEngravable: !!(attributes.ENGRAVABLE && attributes.ENGRAVABLE !== 'FALSE' && isEngravingActive),
    isCustomizable: !!(attributes.CUSTOMIZABLE && attributes.CUSTOMIZABLE !== 'FALSE' && isRemixActive),
    isRoxable: attributes.RX_FILTER === 'TRUE' && isRoxableActive,
    isStella: attributes.PRODUCT_TYPE === 'Electronics',
    isVMAvailable: !!(attributes.VTO_ENABLED && attributes.VTO_ENABLED !== 'FALSE'),
    egiftCard: attributes.MODEL_NAME === 'Gift Card Digital',
    isJunior: Array.isArray(attributes.GENDER) ? attributes.GENDER.includes('CHILD') : attributes.GENDER === 'CHILD',
    isLoyaltyItem: isLoyaltyActive && attributes.CUSTOMER_TYPE === 'Loyalty',
    isOutOfStock: (item?.inventoryQuantity === 0 && item?.lifecycle) || false,
    isTransition: attributes.LENS_CERTIFIED_TECHNOLOGY === 'Transitions' || !!attributes?.LENS_TREATMENT?.includes('Transitions'),
    lifecycle: attributes.LIFECYCLE,
    Status: item.status,
    wishlistRelatedLinks: { isInWishlist: false },
    isFindInStore: item.isFindInStore || undefined,
    isEmailMeWhenAvailable: item.isEmailMeWhenAvailable || undefined,
    juniorLabel: item.juniorLabel || undefined,
    roxableLabel: item.roxableLabel || undefined,
    badgeBelowModelName: item.badgeBelowLeft || undefined,
    showIconCategory: (item.badgeBelowRight?.iconIdentifier) || false,
  }
}

const resolveBadgesAndCategory = (item, { saleBadgeActive, isRemixActive, isEngravingActive, isRoxableActive, sortBadgePath, sortDiscountPath }, { isOfferPriceDateValid }, userData = []) => {
  const { attributes } = item
  const isElectronics = attributes?.PRODUCT_TYPE?.toLowerCase() === 'electronics'
  const showSaleBadge = saleBadgeActive && Boolean(item[sortBadgePath]) && isOfferPriceDateValid
  return {
    categoryItem: isElectronics ? 'Electronics' : attributes.GENDER === 'CHILD' ? 'RAYBANJUNIOR' : attributes.BRAND.toUpperCase(),
    category: getBadgeBelowRight(item, isRemixActive, isEngravingActive, isRoxableActive, showSaleBadge ? item[sortBadgePath] : null) || undefined,
    saleBadge: showSaleBadge ? getSaleBadge(item[sortBadgePath]) : undefined,
    discountAmount: item[sortDiscountPath] || null,
    badgeMeta: getBadgeMeta(item, userData),
  }
}

const resolveVariant = (variantItem, config, locale, currency, filterOnModel, componentSection, userData = []) => {
  const { priceNameConfig, saleBadgeActive, sortBadgePath, isRemixActive, isEngravingActive, isRoxableActive, installmentsFeatureActive, installmentsOrder } = config
  const isLoyaltyActive = sessionStorage.getItem('isLoyaltyProgramEnabled') === 'true'
  const shouldRemoveOnModel = componentSection === 'plp-search' || filterOnModel

  const variantDiscountedPricePath = discountedPriceValue(variantItem.prices, priceNameConfig)
  const priceValue = getPropertyByPath(variantItem.prices, priceNameConfig.ListPrice) || listPriceFallbackValue(variantItem.prices)
  const offerPriceValue = getPropertyByPath(variantItem.prices, variantDiscountedPricePath) || undefined
  const isOfferPriceDateValid = offerDateValid(variantItem.prices, variantDiscountedPricePath)
  const isOnSale = !!(offerPriceValue && offerPriceValue < priceValue && isOfferPriceDateValid)

  const price = isOnSale
    ? getAlgoliaFormattedPrice(priceValue, locale, currency)
    : priceValue
      ? getAlgoliaFormattedPrice(priceValue, locale, currency)
      : offerPriceValue
        ? getAlgoliaFormattedPrice(offerPriceValue, locale, currency)
        : i18n.t('plp_pending_price')

  const showVariantSaleBadge = saleBadgeActive && Boolean(variantItem[sortBadgePath]) && isOfferPriceDateValid
  const variantImages = getPlpImages(variantItem, shouldRemoveOnModel, 'color-variants')

  return {
    imageIsOnModel: getPlpImages(variantItem, false, componentSection)?.some(img => img.name === 'On model') || false,
    img: plpImg(variantImages[0]?.url),
    imgHover: plpImg(getPlpImages(variantItem, filterOnModel)[1]?.url),
    onModelImage: variantItem?.attachments?.find(a => a.name === 'On model')?.url,
    price,
    frameColor: facetFirst(variantItem.attributes_translated?.FRONT_COLOR_FACET),
    lensColor: facetFirst(variantItem.attributes_translated?.LENS_COLOR_FACET),
    offerPrice: isOnSale ? getAlgoliaFormattedPrice(offerPriceValue, locale, currency) : undefined,
    pdpURL: buildAlgoliaProductUrl(variantItem.url),
    catentryId: variantItem.productId,
    upc: variantItem.partnumberId || undefined,
    objectID: variantItem.objectID || undefined,
    isPolarized: !!(variantItem.attributes.POLARIZED && variantItem.attributes.POLARIZED !== 'FALSE'),
    isVMAvailable: !!(variantItem.attributes.VTO_ENABLED && variantItem.attributes.VTO_ENABLED !== 'FALSE'),
    isLoyaltyItem: isLoyaltyActive && variantItem.attributes.CUSTOMER_TYPE === 'Loyalty',
    saleBadge: showVariantSaleBadge ? getSaleBadge(variantItem[sortBadgePath]) : undefined,
    category: getBadgeBelowRight(variantItem, isRemixActive, isEngravingActive, isRoxableActive, showVariantSaleBadge ? variantItem[sortBadgePath] : null) || undefined,
    badgeMeta: getBadgeMeta(variantItem, userData),
    installments: variantItem.attributes?.INSTALLMENTS_PLP === 'true' && installmentsFeatureActive
      ? calculateInstallments(isOnSale ? offerPriceValue : priceValue, installmentsOrder, locale, currency)
      : null,
  }
}

// function used to normalize the single product from Algolia plp
export const getAlgoliaProductNormalized = (
  inputItem,
  filterOnModel = false,
  componentSection = 'classic',
  isWithoutVariants = false,
  userData = []
) => {
  loadSectionTranslations('plp')
  const config = getConfig()
  const locale = getCurrentLocale() || 'en-US'
  const currency = inputItem.prices.LISTPRICE?.currency || 'USD'
  const pricingState = buildPricingState(inputItem.prices, config.priceNameConfig, locale, currency)

  const colorsNumber = inputItem.colors?.length
    ? `${inputItem.colors.length} ${i18n.tc('plp_colors', inputItem.colors.length)}`
    : undefined

  return {
    ...resolveIdentifiers(inputItem),
    ...resolvePricing(pricingState),
    ...resolveImages(inputItem, filterOnModel, componentSection),
    ...resolveFlags(inputItem, config),
    ...resolveBadgesAndCategory(inputItem, config, pricingState, userData),
    catentryId: inputItem.productId || undefined,
    objectID: inputItem.objectID || undefined,
    attachments: inputItem.attachments || [],
    isWithoutVariants,
    colorsNumber,
    colorCount: colorsNumber,
    LensType: 'PLANO',
    productCategory: inputItem.attributes.PRODUCT_TYPE,
    installments: inputItem.attributes?.INSTALLMENTS_PLP === 'true' && config.installmentsFeatureActive
      ? calculateInstallments(parseFloat(pricingState.priceRaw), config.installmentsOrder, locale, currency)
      : null,
    ...(config.installmentFeatureActive && {
      installmentsInfo: getInstallmentMessage(locale, currency, pricingState.priceRaw),
    }),
    ...(inputItem.colors?.length > 1 && {
      colors: inputItem.colors.map(v => resolveVariant(v, config, locale, currency, filterOnModel, componentSection, userData)),
    }),
  }
}
function getLangValue(attributesValue, attributesTranslatedValue) {

  if (attributesValue === attributesTranslatedValue) {
    return attributesTranslatedValue;
  }


  const lastIndex = attributesTranslatedValue.lastIndexOf(attributesValue)

  if (lastIndex !== -1 && lastIndex + attributesValue.length === attributesTranslatedValue.length) {
    return attributesTranslatedValue.slice(0, lastIndex).replace(/\|$/, '')
  }
  return attributesTranslatedValue;
}

function getPlpImages(product, filterOnModel = false, componentSection = 'classic') {
  let plpImages = product.attachments?.filter(attachment => attachment.rule === 'PLP')

  // if active remove the on-model images
  if (filterOnModel) {
    const filteredImages = filterImages(product.attachments, 'name')
    const imageRule = componentSection === 'color-variants' ? 'THUMBNAIL' : 'PLP'
    plpImages = filteredImages?.filter(attachment => attachment.rule === imageRule)
  }

  if (componentSection === 'rtr-wall') {
    const filteredImages = filterImages(product.attachments, 'name')
    plpImages = filteredImages?.filter(attachment => attachment.rule === 'THUMBNAIL' && attachment.name === 'Front')
  }

  // sort images based on the sequence
  const sortedPlpImages = plpImages?.sort((a, b) => a.sequence - b.sequence)

  return sortedPlpImages ?? []
}

export const getSaleBadge = valueBadge => {
  const { saleBadgeColor, saleBadgeImgName, saleBadgeImgPath } = window.algoliaConfig || {}
  const saleBadge = {
    badgeColor: saleBadgeColor && JSON.parse(saleBadgeColor)[valueBadge],
    badgeImage:
      saleBadgeImgName && saleBadgeImgPath && JSON.parse(saleBadgeImgName)[valueBadge]
        ? saleBadgeImgPath + JSON.parse(saleBadgeImgName)[valueBadge]
        : undefined,
    badgeValue: valueBadge || undefined,
  }

  return saleBadge
}

function calculateCountSum(range, priceGuest, priceStats) {
  if (!priceGuest) {
    return 0
  }
  let pricesArray = Object.entries(priceGuest)
  //add min and max to pricesArray because priceRange is incomplete due to maxvaluefacet api limited to 100
  pricesArray.push([priceStats?.min, 1])
  pricesArray.push([priceStats?.max, 1])
  let elementsInRange = pricesArray
    .filter(([key, value]) => {
      let keyPars = parseFloat(key)
      return keyPars >= range[0] && keyPars <= range[1]
    })
    .map(([key, value]) => ({ key, value }))
  let sum = elementsInRange.reduce(
    (accumulator, currentValue) => accumulator + currentValue.value,
    0
  )
  return sum
}

function parseToggleFilters(toggleFilters) {
  try {
    const parsedResult = Object.keys(toggleFilters).map(key => {
      const toggleFilterKey = toggleFilters[key]
      const keyTranslated = i18n.t(key)
      const keyTranslatedLabel =i18n.t(`${key}_toggle`)  || i18n.t(`${key}`)
      const toggleFilterId = algoliaToggleFilters[key].count
      return {
        id: keyTranslated + ':' + toggleFilterId,
        count:
          toggleFilterKey[algoliaToggleFilters[key].count] !== undefined
            ? toggleFilterKey[algoliaToggleFilters[key].count]
            : 0,
        facetIdentifier: keyTranslated,
        algoliaFilterId: key,
        label: keyTranslatedLabel,
      }
    })
    return parsedResult?.length > 0
      ? [
        {
          groupName: 'toggleFilters',
          label: 'toggleFilters',
          list: parsedResult,
        },
      ]
      : []
  } catch (error) {
    console.error('Error parsing toggle filters', error)
    return []
  }
}

export const getAlgoliaPlpFacetsNormalized = async (payload, orderBy = 'default') => {
  const { facets, facets_stats = [], nbHits, hits, userData } = payload
  const res = {}
  if (nbHits == 0) {
    return { numResult: 0, userData: userData || [] }
  }
  const { locale } = wcs.getStoreConfig()
  let pilotDashFilterName = ''
  const { isEarlyAccess, isEngravingActive, isRemixActive, isRoxableActive, sortPrice } = getConfig()

  //build facetPrice id (used for facet match and also for url update):
  let facetPrice = {}
  rangePrice[locale]?.forEach(e => {
    if (e.length > 1) {
      let smalPrice = String(e[0]).length >= 4 ? Number(e[0]) : e[0]
      let igPrice = String(e[1]).length >= 4 ? Number(e[1]) : e[1]
      facetPrice[`${smalPrice}TO${igPrice}`] = calculateCountSum(
        [e[0], e[1]],
        facets[sortPrice],
        facets_stats[sortPrice]
      )
    } else {
      let priceSingle = String(e[0]).length >= 4 ? Number(e[0]) : e[0]
      facetPrice[`>${priceSingle}`] = calculateCountSum(
        [e[0], Infinity],
        facets[sortPrice],
        facets_stats[sortPrice]
      )
    }
  })

  facets.price = facetPrice

  const computeSaleFromDiscount = discountFacet => {
    let zeroValue = 0, sum = 0
    for (const [k, v] of Object.entries(discountFacet)) {
      if (k === '0.0') zeroValue = v
      else if (parseFloat(k) > 0) sum += v
    }
    return { FALSE: zeroValue, TRUE: sum }
  }

  // Remap facets: split multi-value keys (CHANNEL_DETAILS, FRAME_SHAPE_FACET),
  // suppress inactive feature keys, and derive SALE from discount facets.
  const updatedFacets = Object.entries(facets).reduce((result, [key, value]) => {
    if (key === 'attributes_translated.CHANNEL_DETAILS') {
      Object.entries(value).forEach(([subKey, subValue]) => {
        if (subKey === 'EXCLUSIVE SGH' || subKey === 'ONLINE ONLY')
          result[`${key}?${subKey}`] = { [subKey]: subValue }
      })
    } else if (key === 'attributes_translated.FRAME_SHAPE_FACET' && !isChanel) {
      Object.entries(value).forEach(([subKey, subValue]) => {
        const subKeyEn = subKey.includes('|') ? subKey.split('|')[1] : subKey
        if (subKeyEn === 'Pilot') {
          pilotDashFilterName = subKey
          result[`${key}?${subKeyEn}`] = { [subKey]: subValue }
        } else {
          result[key] = value
        }
      })
    } else if (key === 'attributes_translated.CUSTOMIZABLE' && !isRemixActive) {
      // suppress
    } else if (key === 'attributes_translated.ENGRAVABLE' && !isEngravingActive) {
      // suppress
    } else if (key === 'attributes_translated.RX_FILTER' && !isRoxableActive) {
      // suppress
    } else if ((key === 'sortDiscount_Guest' || key === 'sortDiscount_EarlyAccess') && !isChanel) {
      // create combined SALE filter from discount facets
      if (isEarlyAccess) {
        result['attributes_translated.SALE'] = computeSaleFromDiscount(facets['sortDiscount_EarlyAccess'])
      } else if (key === 'sortDiscount_Guest') {
        result['attributes_translated.SALE'] = computeSaleFromDiscount(facets['sortDiscount_Guest'])
      }
    } else {
      result[key] = value
    }
    return result
  }, {})

  const facetsDash = Object.fromEntries(
    Object.entries(updatedFacets).filter(([key]) =>
      algoliaDashFilters[key] &&
      (dashFiltersSequenceArr.length === 0 || dashFiltersSequenceArr.includes(algoliaDashFilters[key].sortFlag))
    )
  )
  const toggleFilters = Object.fromEntries(Object.entries(updatedFacets).filter(([key]) => algoliaToggleFilters[key]))
  const mergedFiltersToOmit = { ...algoliaDashFilters, ...algoliaToggleFilters }
  const facetsWithoutDash = Object.fromEntries(Object.entries(updatedFacets).filter(([key]) => !mergedFiltersToOmit[key]))

  const iconTranslation = id => {
    return id.includes('|') ? id.split('|')[1] : id
  }
  const descTranslation = id => {
    let descTranslation = ''
    //add exception for photocromatic which is hardcoded from attributes to facets options
    if (id === i18n.t('attributes_translated.PHOTOCHROMIC')) descTranslation = 'Photocromatic'
    else descTranslation = id.includes('|') ? id.split('|')[1] : id
    return descTranslation
  }
  const capitalizeWords = str => str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase())

  const labelTranslation = (id, key) => {
    //build label for price filters
    var labelPriceOption = ''
    if (key == 'price') {
      let price_over = 'Over'
      if (!getCurrentLocale().startsWith('en')) {
        price_over = i18n.t('plp_price_over')
      }
      labelPriceOption =
        `${getCurrencySymbol()} ` + id.replace('TO', ' - ').replace('>', ` ${price_over} `)
      labelPriceOption = labelPriceOption.replace(/(\d+(\.\d+)?)/g, match =>
        parseFloat(match).toLocaleString(getCurrentLocale())
      )
    }
    let idOriginal = ''
    if (getCurrentLocale() != 'en-US') {
      if (id.includes('|')) {
        const idParts = id.split('|')
        id = idParts[0]
        idOriginal = idParts[1]
      }
    }
    if (key === 'attributes_translated.PRODUCT_TYPE' && id === 'SUN') {
      return i18n.t('plp_sunglasses')
    }
    if (key === 'attributes_translated.PRODUCT_TYPE' && id === 'Electronics') {
      return i18n.t('plp_smart_glasses')
    }

    if (key === 'attributes_translated.ELECTRONICS_TECHNOLOGY') {
      const techId = (idOriginal || id || '').toString().trim()
      const normalizedTech = techId.toLowerCase()

      if (normalizedTech.includes('camera')) return i18n.t('plp_smart_glasses_camera')
      if (normalizedTech.includes('audio')) return i18n.t('plp_smart_glasses_audio')

      return techId
    }

    let labelTranslated = ''

    const idForKey = idOriginal || id
    const keyLabel = `${key}_Label_${idForKey.replace(/[ ,_]+/g, '_')}`

    if ((key === 'attributes_translated.RECOMMENDED_USE' || key === 'attributes_translated.PROD_HIERARCHY_2' || key === 'attributes_translated.LENS_TREATMENT_FACET' || getCurrentLocale() == 'fr-CA' || getCurrentLocale() == 'es-ES') && i18n.te(keyLabel)) {
      labelTranslated = i18n.t(keyLabel)
    } else {
      labelTranslated = id
    }
    return key === 'attributes_translated.GENDER'
      ? capitalizeWords(labelTranslated)
      : key === 'price'
        ? labelPriceOption
        : labelTranslated
  }
  const hasIcon = key => {
    return (
      [
        'attributes_translated.FRONT_COLOR_FACET',
        'attributes_translated.FRAME_SHAPE_FACET',
        'attributes_translated.LENS_COLOR_FACET',
      ].includes(key) && !isChanel
    )
  }

  //delete filters or options used but not needed in UI
  if (facetsWithoutDash['attributes_translated.GENDER']) {
    const unisexEntry = Object.entries(facetsWithoutDash['attributes_translated.GENDER'])?.find(
      ([key, value]) => key.toUpperCase().includes('UNISEX')
    )
    unisexEntry ? delete facetsWithoutDash['attributes_translated.GENDER'][unisexEntry[0]] : null
  }

  delete facetsWithoutDash['attributes_translated.GENDER']?.Men
  delete facetsWithoutDash['attributes_translated.GENDER']?.Women
  delete facetsWithoutDash['attributes_translated.GENDER']?.Kids

  //delete data used but not needed in UI
  delete facetsWithoutDash['sortDiscount_Guest']
  delete facetsWithoutDash['sortDiscount_EarlyAccess']
  delete facetsWithoutDash['attributes_translated.ON_SALE']
  delete facetsWithoutDash['attributes_translated.ON_EA_SALE']

  res.facetsWithoutDashList = Object.entries(facetsWithoutDash).map(([key, value]) => {
    const keyTranslated = key === 'price' ? 'price' : i18n.t(key)
    const label = key === 'price' ? i18n.t('plp_price') : keyTranslated
    return {
      groupName: key,
      label: label,
      list: Object.entries(value).map(([id, count]) => {
        let desc = null
        const descKey = `${key}_${descTranslation(id.replaceAll(/ /g, '_'))}`
        if (i18n.te(descKey)) desc = i18n.t(descKey)
        return {
          id: keyTranslated + ':' + id,
          count,
          icon: hasIcon(key) ? iconTranslation(id.toLowerCase()).replace(/[ \/]/g, '-') : undefined,
          iconSize: hasIcon(key) ? 'small' : undefined,
          facetIdentifier: keyTranslated + ':' + id,
          label: labelTranslation(id, key),
          desc: desc,
        }
      }),
    }
  })

  const groupsToDelete = ['sortPrice_EarlyAccess', 'sortPrice_Guest']
  // Change position of Price to sortPrice in filter
  groupsToDelete.forEach(groupName => {
    let indexToDelete = res.facetsWithoutDashList.findIndex(item => item.groupName === groupName)
    const indexToExchange = res.facetsWithoutDashList.findIndex(item => item.groupName === 'price')
    // Swap the objects at the specified indexes
    if (indexToDelete !== -1 && indexToExchange !== -1)
      [res.facetsWithoutDashList[indexToDelete], res.facetsWithoutDashList[indexToExchange]] = [
        res.facetsWithoutDashList[indexToExchange],
        res.facetsWithoutDashList[indexToDelete],
      ]

    indexToDelete = res.facetsWithoutDashList.findIndex(item => item.groupName === groupName)
    if (indexToDelete !== -1) {
      res.facetsWithoutDashList.splice(indexToDelete, 1)
    }
  })

  if (Object.keys(facetsDash).length > 0) {
    res.facetsDashList = [
      {
        groupName: 'dashCheckBoxes',
        label: 'dashCheckBoxes',
        list: Object.entries(facetsDash).map(([key, value]) => {
          const facetsDashKey =
            key === 'attributes_translated.FRAME_SHAPE_FACET?Pilot'
              ? Object.fromEntries(Object.entries(facetsDash[key]).map(([k, v]) => [k.split('|').pop(), v]))
              : facetsDash[key]
          const facetsDashId =
            key === 'attributes_translated.FRAME_SHAPE_FACET?Pilot'
              ? pilotDashFilterName
              : algoliaDashFilters[key].count
          const keyValue =
            key === 'attributes_translated.FRAME_SHAPE_FACET?Pilot'
              ? 'attributes_translated.FRAME_SHAPE_FACET'
              : key

          return {
            id: i18n.t(`${keyValue}`) + ':' + facetsDashId,
            count:
              facetsDashKey[algoliaDashFilters[key].count] !== undefined
                ? facetsDashKey[algoliaDashFilters[key].count]
                : 0,
            icon: algoliaDashFilters[key].icon,
            iconSize: 'small',
            facetIdentifier: i18n.t(`${keyValue}`) + ':' + facetsDashId,
            label: i18n.t(`${key}`),
            sortFlag: algoliaDashFilters[key].sortFlag,
          }
        }),
      },
    ]
    //sorted manually pilot and exclusive dash filter (not with dashboard)
    res.facetsDashList = sortFacetDash(res.facetsDashList)

    res.filterOptionsList = [...res.facetsDashList, ...res.facetsWithoutDashList]
  } else {
    res.filterOptionsList = res.facetsWithoutDashList
  }
  res.filterOptionsList = [...res.filterOptionsList, ...parseToggleFilters(toggleFilters)]
  res.facetsToggleList = parseToggleFilters(toggleFilters)
  res.numResult = nbHits

  //todo understand how Algolia sends us sort options
  let sortOptionsList = [
    {
      id: '1',
      label: i18n.t('plp_newest'),
    },
    {
      id: 'default',
      label: i18n.t('plp_suggested'),
    },
    {
      id: '3',
      label: i18n.t('plp_low_high'),
    },
    {
      id: '4',
      label: i18n.t('plp_high_low'),
    },
  ]
  res.sortOptionsList = sortOptionsList.map((item = {}) => ({
    ...item,
    selected: item.id === orderBy,
  }))
  return {
    ...res,
    ...payload,
    hits,
  }
}

export const setLocale = locale => {
  return locale == 'es-ES' ? 'de-DE' : locale
}

const offerDateValid = (price, path) => {
  const pathNoOfferPrice = path.replace(/\.offerPrice$/, '')
  const startDate = price[pathNoOfferPrice]?.startDate
  const endDate = price[pathNoOfferPrice]?.endDate
  return startDate && endDate ? isDateValid(startDate, endDate) : false
}

const dateToUTC = date => {
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  )
}

export const isDateValid = (start, end) => {
  const currentDateUTC = dateToUTC(new Date())
  const arrStart = start.split(/[- :]/)
  const arrEnd = end.split(/[- :]/)
  const startDate = new Date(
    arrStart[0],
    arrStart[1] - 1,
    arrStart[2],
    arrStart[3],
    arrStart[4],
    arrStart[5]
  )
  const endDate = new Date(arrEnd[0], arrEnd[1] - 1, arrEnd[2], arrEnd[3], arrEnd[4], arrEnd[5])

  return currentDateUTC >= startDate && currentDateUTC <= endDate
}
export const getInstallmentMessage = (locale, currency, price) => {
  const minTotalAmount = window.wcs_feature_paymentInstallment.minTotalAmount,
    maxInstallmentAmount = window.wcs_feature_paymentInstallment.maxInstallmentAmount

  let values = ''
  if (minTotalAmount != undefined && maxInstallmentAmount != undefined) {
    let minTotalAmountFmt = parseFloat(minTotalAmount),
      maxInstallmentAmountFmt = parseFloat(maxInstallmentAmount),
      installmentCount = 1
    for (let i = 1; i <= maxInstallmentAmountFmt; i++) {
      let currentPrice = price / i
      if (currentPrice >= minTotalAmountFmt) {
        installmentCount = i
      }
    }
    let priceToRender = getAlgoliaFormattedPrice(price / installmentCount, locale, currency)
    values = i18n.t('plp_installments_info', [installmentCount, priceToRender])
  }
  return values
}

export const getAlgoliaFormattedPrice = (price, locale, curr, priceWithoutSymbol = false, applyFrCaFormat = true) => {
  const customLocale = setLocale(locale)

  // Detect Safari
  let safariAgent = navigator.userAgent.indexOf('Safari') > -1
  let chromeAgent = navigator.userAgent.indexOf('Chrome') > -1
  // Discard Chrome since it also matches Safari
  if (chromeAgent && safariAgent) safariAgent = false
  const currencyDisplayValue = safariAgent ? 'symbol' : 'narrowSymbol'

  let numberFormat = new Intl.NumberFormat(customLocale, {
    style: 'currency',
    currency: curr,
    currencyDisplay: currencyDisplayValue,
  })
  let formattedPrice = numberFormat.format(parseFloat(formatPrice(price), 10))
  const symbol = numberFormat.formatToParts().find(part => part.type === 'currency').value

  if (applyFrCaFormat && customLocale == 'fr-CA' && formattedPrice.length >= 7) {
    formattedPrice = formattedPrice.replace(',', '.').replace(/\s/g, ',')
    formattedPrice = formattedPrice.replace(`,${symbol}`, '')
  }

  const countryListArr = ['de-DE', 'fr-CA', 'fr-FR', 'es-ES']
  const priceNum = `${formattedPrice.replace(symbol, '').trim()}`

  if (priceWithoutSymbol) {
    return priceNum
  }

  return countryListArr.includes(customLocale) ? `${priceNum}${symbol}` : `${symbol}${priceNum}`
}

export const getBadgeBelowRight = (item, isRemixActive, isEngravingActive, isRoxableActive, sortBadgeValue) => {
  loadSectionTranslations('plp')
  const { categories, attributes_translated } = item
  const {
    CHANNEL_DETAILS,
    RUNWAY,
    LAUNCH_DATE_CODE,
    LIMITED_EDITION,
    AVANT_PREMIERE_DATE,
    SPECIAL_PROJECT_TYPE,
    CUSTOMER_TYPE,
    LENS_CERTIFIED_TECHNOLOGY,
    POLARIZED,
    CUSTOMIZABLE,
    ENGAVABLE,
    RX_FILTER,
  } = attributes_translated

  let isOutlet = false
  let hasCategoryBestSeller = false
  const outletCategory = window?.algoliaConfig?.outletBadgeCategory

  categories.forEach((category) => {
    const normalizedVal = category.toLowerCase()
    if (normalizedVal.startsWith('best_seller')) {
      hasCategoryBestSeller = true
    }

    if (normalizedVal === outletCategory) {
      isOutlet = true
    }
  })

  const { inventoryQuantity, lifecycle } = item
  const now = Date.now()

  //badges priority conditions
  switch (true) {
    //priority 1 product on sale
    case Boolean(sortBadgeValue):
      return isOutlet ? `${i18n.t('plp_badge_outlet')} ${sortBadgeValue}` : sortBadgeValue
    //priority 2 sold out - 3 coming soon - 4 coming soon back
    case inventoryQuantity == 0: {
      const isChannelOnlineOnlyOrEmpty = !CHANNEL_DETAILS || CHANNEL_DETAILS === 'ONLINE ONLY'

      // SOLD OUT: Lifecycle inactive, stock 0, channel online only or empty
      if (!lifecycle && isChannelOnlineOnlyOrEmpty) {
        return i18n.t('plp_badge_sold_out')
      }

      // COMING SOON: Lifecycle active, stock 0, channel "COMING SOON"
      if (lifecycle && CHANNEL_DETAILS === 'COMING SOON') {
        return i18n.t('plp_badge_coming_soon')
      }

      // COMING BACK SOON: Lifecycle active, stock 0, channel online only or empty
      if (lifecycle && isChannelOnlineOnlyOrEmpty) {
        return i18n.t('plp_badge_coming_back')
      }

      return null
    }
    //priority 5 sun club
    case CUSTOMER_TYPE === 'Loyalty':
      return i18n.t('attributes_translated.CUSTOMER_TYPE')
    //priority 6 transitions
    case LENS_CERTIFIED_TECHNOLOGY === 'Transitions':
      return i18n.t('attributes_translated.LENS_CERTIFIED_TECHNOLOGY')
    //priority 7 polarized
    case POLARIZED === 'Polarized':
      return i18n.t('attributes_translated.POLARIZED')
    //priority 8 new
    case LAUNCH_DATE_CODE && window.algoliaConfig?.newBadgeDateList?.includes(LAUNCH_DATE_CODE):
      return i18n.t('plp_badge_new')
    //priority 9 best seller
    case hasCategoryBestSeller:
      return i18n.t('attributes_translated.MOST_WANTED_STYLES')
    //priority 10 online only
    case CHANNEL_DETAILS === 'ONLINE ONLY':
      return i18n.t('attributes_translated.CHANNEL_DETAILS?ONLINE ONLY')
    //priority 11 exclusive
    case ['SGH EXCLUSIVE', 'EXCLUSIVE SGH'].includes(CHANNEL_DETAILS):
      return i18n.t('attributes_translated.CHANNEL_DETAILS?EXCLUSIVE SGH')
    //priority 12 only at sunglasshut
    case ['ONLY AT', 'AT ONLY'].includes(CHANNEL_DETAILS):
      return i18n.t('attributes_translated.CHANNEL_DETAILS?ONLY AT')
    //priority 13 find it here first
    case AVANT_PREMIERE_DATE && Date.parse(AVANT_PREMIERE_DATE) > now:
      return i18n.t('plp_badge_find_it_here_first')
    //priority 14 limited edition
    case LIMITED_EDITION === 'TRUE':
      return i18n.t('attributes_translated.LIMITED_EDITION')
    //priority 15 collaboration
    case SPECIAL_PROJECT_TYPE && window.algoliaConfig?.collectionType?.includes(SPECIAL_PROJECT_TYPE.toLowerCase()):
      return i18n.t('plp_badge_collaboration')
    //priority 16 runway
    case RUNWAY === 'TRUE':
      return i18n.t('plp_badge_runway')
    //priority 17 prescription
    case RX_FILTER === 'TRUE' && isRoxableActive:
      return i18n.t('attributes_translated.RX_FILTER')
    //priority 18 customize
    case CUSTOMIZABLE === 'TRUE' && isRemixActive:
      return i18n.t('attributes_translated.CUSTOMIZABLE')
    //priority 19 engravable
    case ENGAVABLE === 'TRUE' && isEngravingActive:
      return i18n.t('attributes_translated.ENGRAVABLE')
    default:
      return null
  }
}

export const discountedPriceValue = (price, { EarlyAccess, OfferPrice }) => {
  const earlyAccessPrice = getPropertyByPath(price, EarlyAccess)
  const offerPrice = getPropertyByPath(price, OfferPrice)
  const earlyAccessDateValid = offerDateValid(price, EarlyAccess)
  const offerPriceDateValid = offerDateValid(price, OfferPrice)

  if (
    getConfig().isEarlyAccess &&
    earlyAccessPrice &&
    earlyAccessDateValid &&
    (!offerPrice || !offerPriceDateValid || parseFloat(earlyAccessPrice) < parseFloat(offerPrice))
  )
    return EarlyAccess

  return OfferPrice
}

export const listPriceFallbackValue = prices => {
  const { priceNameConfig } = getConfig()
  return getPropertyByPath(prices, priceNameConfig.OfferPrice) || ''
}

export const sortFacetDash = facetsDashList => {
  facetsDashList[0].list.sort((a, b) => {
    const indexA = dashFiltersSequenceArr.indexOf(a.sortFlag)
    const indexB = dashFiltersSequenceArr.indexOf(b.sortFlag)

    return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB)
  })

  return facetsDashList
}

export const getAlgoliaContentNormalized = responseResult => {
  const countryChunk = `/${getCurrentCountry()}`
  const content = []
  const { hits } = responseResult
  const paginationInfo = {}

  paginationInfo.totalItems = responseResult.nbHits || undefined
  paginationInfo.currentPage = responseResult.page + 1 || undefined
  paginationInfo.totalPages = responseResult.nbPages || undefined
  paginationInfo.pageSize = responseResult.hitsPerPage || undefined

  content.push(
    ...hits.map(hit => {
      const titleText = hit._highlightResult?.title?.value || hit.title || ''
      return {
        headingTitle: titleText,
        linkUrl: `${countryChunk}${hit?.url}`,
        id: hit?.objectID,
      }
    })
  )

  return {
    paginationInfo,
    content,
  }
}

export const getAlgoliaTopBrandsNormalized = (response, isRule = false) => {
  const brands = []
  const originalBrands = isRule ? response.userData[0].facets : response.hits
  originalBrands.forEach(element => {
    const brandValue = isRule ? element : element.facetValue
    const brandImage = getBrandsDetails(brandValue).image
    if (brandImage !== undefined && brands.length < 6) {
      brands.push({
        alt: `${brandValue}`,
        id: 'SearchPanel_Content_link',
        imageSrc: brandImage,
        link: `/${getCurrentCountry()}/${getBrandsDetails(brandValue).link}`,
      })
    }
  })

  return brands
}

const BRAND_PATH = '//assets.sunglasshut.com/extra/image/LuxotticaRetail/SGH_brands_logos/'
const CHANEL_PATH = '/wcsstore/SGH/experiences/UK/chanel/'

const BRANDS_DETAILS = {
  'Alaia':                { image: `${BRAND_PATH}ALAIA.png`,                       link: 'alaia' },
  'Alain Mikli':          { image: `${BRAND_PATH}AM%20Logo_bk_new.png`,            link: 'alain-mikli' },
  'Armani Exchange':      { image: `${BRAND_PATH}armani-exchange_bl_18.png`,       link: 'armani-exchange' },
  'Arnette':              { image: `${BRAND_PATH}arnette_bl_18.png`,               link: 'arnette' },
  'Balenciaga':           { image: `${BRAND_PATH}Balenciaga_bl.png`,               link: 'balenciaga' },
  'Bottega Veneta':       { image: `${BRAND_PATH}Bottega_Veneta_Black_bl_18.png`,  link: 'bottega-veneta' },
  'Brunello Cucinelli':   { image: `${BRAND_PATH}brunellocucinelli_bl_18.png`,     link: 'brunello-cucinelli' },
  'Burberry':             { image: `${BRAND_PATH}burberry_bl_18.png`,              link: 'burberry' },
  'Bvlgari':              { image: `${BRAND_PATH}bulgari_bl_18.png`,               link: 'bvlgari' },
  'Cartier':              { image: `${BRAND_PATH}Cartier.png`,                     link: 'cartier' },
  'Celine':               { image: `${BRAND_PATH}celine_bl_18.png`,               link: 'celine' },
  'Chanel':               { image: `${CHANEL_PATH}CHANELlogo.jpg`,                link: 'chanel' },
  'Chloé':                { image: `${BRAND_PATH}chloe%5Fbl%5F18.png`,            link: 'chloe' },
  'Coach':                { image: `${BRAND_PATH}coach_bl_18.png`,                link: 'coach' },
  'Costa':                { image: `${BRAND_PATH}costa_bl_18.png`,                link: 'costa' },
  'DIOR':                 { image: `${BRAND_PATH}dior_bl_18.png`,                 link: 'dior' },
  'Diesel':               { image: `${BRAND_PATH}DIESEL_bl_18.png`,               link: 'diesel' },
  'Dolce&Gabbana':        { image: `${BRAND_PATH}dolcegabbana_bl_18.png`,         link: 'dolce-and-gabbana' },
  'EA7':                  { image: `${BRAND_PATH}EA7.png`,                        link: 'ea7' },
  'Emporio Armani':       { image: `${BRAND_PATH}emporio-armani_bl_18.png`,       link: 'emporio-armani' },
  'Fendi':                { image: `${BRAND_PATH}fendi_bl_18.png`,                link: 'fendi' },
  'Ferrari':              { image: `${BRAND_PATH}ferrari_bl_18.png`,              link: 'ferrari' },
  'Giorgio Armani':       { image: `${BRAND_PATH}giorgio-armani_bl_18.png`,       link: 'giorgio-armani' },
  'Gucci':                { image: `${BRAND_PATH}gucci_bl_18.png`,                link: 'gucci' },
  'Jimmy Choo':           { image: `${BRAND_PATH}jimmychoo_bl_18.png`,            link: 'jimmy-choo' },
  'Loewe':                { image: `${BRAND_PATH}loewe_bl_18.png`,                link: 'loewe' },
  'Michael Kors':         { image: `${BRAND_PATH}michael_kors_18.png`,            link: 'michael-kors' },
  'Moncler':              { image: `${BRAND_PATH}moncler_bl_18.png`,              link: 'moncler' },
  'Maui Jim':             { image: `${BRAND_PATH}maui-jim_bl_18.png`,             link: 'maui-jim' },
  'Miu Miu':              { image: `${BRAND_PATH}miu-miu_bl_18.png`,              link: 'miu-miu' },
  'Oakley':               { image: `${BRAND_PATH}Oakley_bl_18.png`,               link: 'oakley' },
  'Oakley Meta':          { image: `${BRAND_PATH}OO_META.png`,                    link: 'oakley-meta' },
  'Oliver Peoples':       { image: `${BRAND_PATH}oliver_peoples_18.png`,          link: 'oliver-peoples' },
  'Persol':               { image: `${BRAND_PATH}persol_bl_18.png`,               link: 'persol' },
  'Polo Ralph Lauren':    { image: `${BRAND_PATH}polo-ralph-lauren_bl_18.png`,    link: 'polo-ralph-lauren' },
  'Prada':                { image: `${BRAND_PATH}prada_bl_18.png`,                link: 'prada' },
  'Prada Linea Rossa':    { image: `${BRAND_PATH}prada-linea-rossa_red_18.png`,   link: 'prada-linea-rossa' },
  'Ralph':                { image: `${BRAND_PATH}ralph_bl_18.png`,                link: 'ralph' },
  'Ralph Lauren':         { image: `${BRAND_PATH}ralph-lauren_bl_18.png`,         link: 'ralph-lauren' },
  'Ray-Ban':              { image: `${BRAND_PATH}ray-ban_wt_18.png`,              link: 'ray-ban' },
  'Ray-Ban Meta':         { image: `${BRAND_PATH}RB_META.png`,                    link: 'ray-ban-meta' },
  'Saint Laurent':        { image: `${BRAND_PATH}YSL_bl.png`,                     link: 'saint-laurent' },
  'Scuderia Ferrari':     { image: `${BRAND_PATH}scuderiaferrari_bl_18.png`,      link: 'scuderia-ferrari' },
  'Sunglass Hut Collection': { image: `${BRAND_PATH}sghcollection_bl_18.png`,    link: 'sunglass-hut-collection' },
  'Swarovski':            { image: `${BRAND_PATH}swarovski_bl.png`,               link: 'swarovski' },
  'Tiffany & Co.':        { image: `${BRAND_PATH}tiffany_bl_18.png`,              link: 'tiffany-co' },
  'Tom Ford':             { image: `${BRAND_PATH}tomford_18.png`,                 link: 'tom-ford' },
  'Tory Burch':           { image: `${BRAND_PATH}tory-burch_bl_18.png`,           link: 'tory-burch' },
  'Versace':              { image: `${BRAND_PATH}LOGO-VERSACE.png`,               link: 'versace' },
  'Vogue Eyewear':        { image: `${BRAND_PATH}VO_LOGO_2020xSGH.png`,          link: 'vogue-eyewear' },
}

export const getBrandsDetails = brandName => BRANDS_DETAILS[brandName] ?? ''

export const normalizeProductPrice = pricesObject => {
  const productPrice = {
    currency: '',
    discountBadge: '',
    discountedPrice: 0,
    formattedDiscountedPrice: '',
    formattedFullPrice: '',
    fullPrice: 0,
    upc: '',
    priceWithoutSymbol: '',
    discountedPriceWithoutSymbol: '',
    loyaltyPoints: null,
    installment: null,
  }

  const locale = getCurrentLocale() || 'en-US'
  const { priceNameConfig } = getConfig()
  const discountObjectName = discountedPriceValue(pricesObject, priceNameConfig)
  const discountedPrice = getPropertyByPath(pricesObject, discountObjectName)
  let fullPricePath = priceNameConfig.ListPrice
  let fullPrice = getPropertyByPath(pricesObject, fullPricePath)
  if (!fullPrice) {
    fullPrice = listPriceFallbackValue(pricesObject)
    fullPricePath = priceNameConfig.OfferPrice
  }
  const currencyPath = fullPricePath.split('.')[0] + '.currency'
  const currency = getPropertyByPath(pricesObject, currencyPath) || 'USD'
  const loyaltyPath = fullPricePath.split('.')[0] + '.loyaltyPoints'
  let loyaltyPoints = getPropertyByPath(pricesObject, loyaltyPath) || null
  const installmentPath = fullPricePath.split('.')[0] + '.installmentText'
  let installment = getPropertyByPath(pricesObject, installmentPath) || null
  const installmentProviderPath = fullPricePath.split('.')[0] + '.installmentProvider'
  let installmentProvider = getPropertyByPath(pricesObject, installmentProviderPath) || null

  productPrice.fullPrice = fullPrice
  productPrice.discountedPrice = discountedPrice
  productPrice.formattedFullPrice = fullPrice
    ? getAlgoliaFormattedPrice(fullPrice, locale, currency)
    : undefined
  productPrice.formattedDiscountedPrice = discountedPrice
    ? getAlgoliaFormattedPrice(discountedPrice, locale, currency)
    : undefined

  productPrice.currency = currency
  productPrice.priceWithoutSymbol = fullPrice
    ? getAlgoliaFormattedPrice(fullPrice, locale, currency, true)
    : undefined
  productPrice.discountedPriceWithoutSymbol = discountedPrice
    ? getAlgoliaFormattedPrice(discountedPrice, locale, currency, true)
    : undefined

  //ged badge
  if (discountedPrice && fullPrice > discountedPrice) {
    productPrice.discountBadge = getPropertyByPath(
      pricesObject,
      discountObjectName.replace(/[\.#].*$/, '.badge')
    )
    loyaltyPoints = getPropertyByPath(
      pricesObject,
      discountObjectName.replace(/[\.#].*$/, '.loyaltyPoints')
    )
    installment = getPropertyByPath(
      pricesObject,
      discountObjectName.replace(/[\.#].*$/, '.installmentText')
    )
    installmentProvider = getPropertyByPath(
      pricesObject,
      discountObjectName.replace(/[\.#].*$/, '.installmentProvider')
    )
  }
  productPrice.loyaltyPoints = Number(loyaltyPoints)
  productPrice.installment = installment
  productPrice.installmentProvider = installmentProvider

  return productPrice
}

export const getProductLabel = (currentProduct = {}) => {
  try {
    switch (true) {
      case currentProduct?.refurbished :
        return 'Refurbished'
      default:
        return ''
    }
  } catch (e) {
    return ''
  }
}


export const normalizeProductDataForAnalytics = (currentProduct = {}, giftCardQuantity = 1) => {
  let productCategory = currentProduct.skuType
  const normalizedVal = productCategory === 'Accessories' ? productCategory.toUpperCase() : productCategory
  return {
    productIndex: currentProduct.upc,
    productData: {
      Status: getProductStatus(true, currentProduct.active, Number(currentProduct.InventoryAvailability?.[0]?.availableQuantity), isExlusive(currentProduct), isLimitEdition(currentProduct)),
      Category: currentProduct.skuType === 'GiftCards' ? 'GIFTCARD' : normalizedVal,
      Label:getProductLabel(currentProduct),
      LensType: 'PLANO',
      Price: normalizeProductPrice(currentProduct.prices).priceWithoutSymbol,
      PriceFull: normalizeProductPrice(currentProduct.prices).priceWithoutSymbol,
      Sku: currentProduct.analytics.prodobj.Sku,
      ModelName: currentProduct.analytics.prodobj.ModelName,
      FrameTechnology: currentProduct.analytics.prodobj.FrameTechnology,
      Brand: currentProduct.analytics.prodobj.Brand,
      Shape: currentProduct.analytics.prodobj.Shape,
      Size: currentProduct.analytics.prodobj.Size,
      LensTechnology: currentProduct.analytics.prodobj.LensTechnology,
      LoyaltyExclusive: currentProduct.analytics.prodobj.LoyaltyExclusive,
      ModelCode: currentProduct.modelCode,
      Url: currentProduct.analytics.prodobj.Url,
      Units: `${giftCardQuantity}` || '1',
      FrameType: '',
      Engraving: '',
      Lens: '',
      Frame: '',
      Case: '',
    },
  }
}


export const normalizeProductObjDataForAnalytics = (currentProduct = {}) => {
  const productPriceObj = normalizeProductPrice(currentProduct.prices)
  const { discountedPrice, fullPrice } = productPriceObj
  const isDiscounted = discountedPrice < fullPrice
  const stock = Number(currentProduct.InventoryAvailability?.[0]?.availableQuantity)
  const status = getProductStatus(currentProduct.active, stock, isExlusive(currentProduct), isLimitEdition(currentProduct))
  return {
    productIndex: currentProduct.upc,
    productData: {
      Sku: currentProduct.analytics?.prodobj.Sku,
      Category: currentProduct.stella
        ? 'Electronics'
        : currentProduct.accessorise
          ? 'ACCESSORIES'
          : currentProduct.gc
            ? 'GIFTCARD'
            : 'SUN',
      Engraving: 'N',
      Status: status,
      Units: currentProduct.analytics?.prodobj.Units || '',
      ModelName: currentProduct.analytics?.prodobj.ModelName,
      Price: isDiscounted ? productPriceObj.discountedPriceWithoutSymbol : productPriceObj.priceWithoutSymbol,
      PriceFull: productPriceObj.priceWithoutSymbol,
      Label:getProductLabel(currentProduct),
      LensColor: currentProduct.analytics?.prodobj.LensColor,
      LensTechnology: currentProduct.analytics?.prodobj.LensTechnology,
      FrameTechnology: currentProduct.analytics?.prodobj.FrameTechnology,
      Shape: currentProduct.analytics?.prodobj.Shape,
      Brand: currentProduct.analytics?.prodobj.Brand,
      Size: currentProduct.analytics?.prodobj.Size,
      pricewithoutengraving: productPriceObj.priceWithoutSymbol,
      pricewithengraving: (Number(productPriceObj.priceWithoutSymbol) + Number(pdp.state.engravingInfo?.engravingData?.unformattedFullPrice || 0)).toString(),
      OosOptions: getOosOptions(status, stock, currentProduct),
      Image: currentProduct.analytics?.prodobj.Image,
      Url: currentProduct.analytics?.prodobj.Url,
      LensType: 'PLANO',
      FrameType: '',
      LoyaltyExclusive: currentProduct.analytics?.prodobj.LoyaltyExclusive,
      Conf_IsUpcSupported: currentProduct.analytics?.prodobj.Conf_IsUpcSupported,
      Conf_RtrIsUpcSupported: currentProduct?.analytics?.prodobj?.Conf_RtrIsUpcSupported
        ? '1'
        : '0',
    },
  }
}

function isLimitEdition(product) {
  return product.badges?.topSection.some(badge => badge.identifier === 'LIMITED_EDITION')
}

function isExlusive(product) {
  return product.badges?.topSection.some(badge => badge.identifier === 'PDP_EXCLUSIVE')
}

function isOnlineOnly(product) {
  return product.badges.topSection.some(
    badge => badge.identifier === 'PDP_ONLINE_ONLY'
  )
}

function getProductStatus(lifecycle, stock, isExclusive, limitedEdition) {
  if (!lifecycle && stock === 0 && !isExclusive && !limitedEdition) {
    return 'Not-for-sale'
  }
  if (lifecycle && stock === 0) {
    return "Out-of-stock"
  }
  if (stock === 0 && (isExclusive || limitedEdition)) {
    return "SoldOut";
  }
  if (lifecycle && stock > 2) {
    return "Available-fewLeft";
  }
  if (lifecycle && stock === 2) {
    return "Available-twoLeft";
  }
  if (lifecycle && stock === 1) {
    return "Available-oneLeft";
  }
  if (stock > 0) {
    return "Available";
  }

  // Add any additional conditions based on the rest of the table.
  return 'Available'
}

function getOosOptions(status, stock, product) {
  let options = []
  const addToBagAction = addToBagButtonAction({
    isAvailableQuantityMoreThanZero: stock > 0,
    isProductActive: product.active,
    isOnlineOnly: isOnlineOnly(product),
    mainProduct: product,
  })
  if (status === "Out-of-stock") {
    if (product.availableForRITC) options.push("FindStore")
    if (product.availableForRemix) options.push("CustomizeIt")
    if (addToBagAction === 'emailMe') options.push("EmailMeWhenAvailable")
    else if (addToBagAction === 'pickup') options.push("BuyNowAndPickUpInStore")
  }

  return options.join(",")
}

export const getAlgoliaStopWords = (algoliaStopWordsResultsArray) => {
  try {
    const stopWordsObjs = algoliaStopWordsResultsArray?.filter(item => item['types'].includes('stopword'))
    return stopWordsObjs?.length > 0 ? stopWordsObjs.flatMap(stopwordItem => stopwordItem['words']) : null
  } catch (error) {
    console.error('Error getting stop words', error)
    return null
  }

}

export const calculateInstallments = (offerPrice, installmentsOrder, locale, currency) => {
  if (!offerPrice || !installmentsOrder) return null

  for (const providerObj of installmentsOrder) {
    const config = Object.values(providerObj)[0]
    const minValue = parseFloat(config.minValue) || 0
    const maxValue = parseFloat(config.maxValue) || Infinity
    const minInstallmentValue = parseFloat(config.minInstallmentValue) || 0
    const rawInstallments = config.numberOfInstallments

    if (offerPrice < minValue || offerPrice > maxValue) continue
    if (!rawInstallments) continue;

    const isMaxBased = typeof rawInstallments === 'string' && rawInstallments.startsWith('<')
    let numberOfInstallments = parseInt(rawInstallments.replace('<', ''), 10)

    if (isNaN(numberOfInstallments) || numberOfInstallments <= 0) continue

    while (numberOfInstallments >= 1) {
      const installmentAmount = offerPrice / numberOfInstallments
      if (installmentAmount >= minInstallmentValue) {
        return getAlgoliaFormattedPrice(installmentAmount, locale, currency, false, false)
      }
      if (isMaxBased) {
        numberOfInstallments--
      } else {
        break
      }
    }
  }

  return null
}

export const getBadgeMeta = (item, userData = []) => {
  if (item.attributes?.PRODUCT_TYPE !== 'Electronics') return undefined

  const badgeChannel = item.attributes_translated?.BADGE_CHANNEL
  const electronicsTech = item.attributes_translated?.ELECTRONICS_TECHNOLOGY?.toLowerCase()
  const showElectronicsTechBadge = userData?.some(d => d.electronics_technology_badge === true)

  if (showElectronicsTechBadge) {
    if (electronicsTech?.includes('audio')) return { label: i18n.t('plp_smart_glasses_audio'), icon: 'smart-glasses-audio' }
    if (electronicsTech?.includes('camera')) return { label: i18n.t('plp_smart_glasses_camera'), icon: 'smart-glasses-camera' }
  }

  if (!badgeChannel) return { label: i18n.t('plp_smart_glasses'), icon: 'smart-glasses-ai' }

  let badgeBrandConfig = null
  try {
    if (window?.algoliaConfig?.brandBadge) {
      badgeBrandConfig = JSON.parse(window.algoliaConfig.brandBadge)
    }
  } catch (error) {
    console.error('Error parsing badgeBrand config:', error)
  }

  if (badgeChannel && badgeBrandConfig) {
    let matchedKey = badgeChannel in badgeBrandConfig
      ? badgeChannel
      : badgeChannel.split('|').find(part => part in badgeBrandConfig || part.trim() in badgeBrandConfig)

    if (matchedKey && !(matchedKey in badgeBrandConfig)) {
      matchedKey = matchedKey.trim()
    }

    if (matchedKey && matchedKey in badgeBrandConfig) {
      return { label: badgeBrandConfig[matchedKey], icon: 'smart-glasses-ai' }
    }
  }

  return undefined
}
